import { InputFormat } from './TextField.props';

/**
 * Auto-formatting function for different input types
 * @param value - The input value to format
 * @param format - The format type to apply
 * @returns The formatted string
 */
export const formatValue = (value: string, format: InputFormat): string => {
  // Remove all non-numeric characters first
  const numeric = value.replace(/\D/g, '');

  switch (format) {
    case 'phone':
      // Format: xxx-xxx-xxxx
      if (numeric.length <= 3) return numeric;
      if (numeric.length <= 6)
        return `${numeric.slice(0, 3)}-${numeric.slice(3)}`;
      return `${numeric.slice(0, 3)}-${numeric.slice(3, 6)}-${numeric.slice(6, 10)}`;

    case 'ssn':
      // Format: xxx-xx-xxxx
      if (numeric.length <= 3) return numeric;
      if (numeric.length <= 5)
        return `${numeric.slice(0, 3)}-${numeric.slice(3)}`;
      return `${numeric.slice(0, 3)}-${numeric.slice(3, 5)}-${numeric.slice(5, 9)}`;

    case 'creditCard':
      // Format: xxxx xxxx xxxx xxxx
      if (numeric.length <= 4) return numeric;
      if (numeric.length <= 8)
        return `${numeric.slice(0, 4)} ${numeric.slice(4)}`;
      if (numeric.length <= 12)
        return `${numeric.slice(0, 4)} ${numeric.slice(4, 8)} ${numeric.slice(8)}`;
      return `${numeric.slice(0, 4)} ${numeric.slice(4, 8)} ${numeric.slice(8, 12)} ${numeric.slice(12, 16)}`;

    case 'zipCode':
      // Format: xxxxx-xxxx
      if (numeric.length <= 5) return numeric;
      return `${numeric.slice(0, 5)}-${numeric.slice(5, 9)}`;

    case 'date':
      // Format: mm/dd/yyyy with validation
      if (numeric.length <= 2) {
        // Validate month (01-12)
        if (numeric.length === 1) {
          // First digit: allow 0-1
          const firstDigit = parseInt(numeric, 10);
          if (firstDigit > 1) {
            // Auto-prefix with 0 for digits 2-9
            return `0${numeric}/`;
          }
          return numeric;
        }
        if (numeric.length === 2) {
          const month = parseInt(numeric, 10);
          // Reject invalid months: 00, 13-99
          if (month < 1 || month > 12) {
            return numeric.slice(0, 1); // Remove invalid second digit
          }
        }
        return numeric;
      }
      if (numeric.length <= 4) {
        const day =
          numeric.length === 4 ? parseInt(numeric.slice(2, 4), 10) : 0;
        // Validate day (01-31)
        if (numeric.length === 4 && (day < 1 || day > 31)) {
          return `${numeric.slice(0, 2)}/${numeric.slice(2, 3)}`; // Remove invalid second digit
        }
        return `${numeric.slice(0, 2)}/${numeric.slice(2)}`;
      }
      // Year validation (length > 4)
      if (numeric.length <= 8) {
        const yearPart = numeric.slice(4, 8);
        // If year is complete (4 digits), validate it (1900-2100)
        if (yearPart.length === 4) {
          const year = parseInt(yearPart, 10);
          // Reject years outside reasonable range (1900-2100)
          if (year < 1900 || year > 2100) {
            return `${numeric.slice(0, 2)}/${numeric.slice(2, 4)}/${numeric.slice(4, 7)}`; // Remove invalid 4th digit
          }
        }
        return `${numeric.slice(0, 2)}/${numeric.slice(2, 4)}/${yearPart}`;
      }
      return `${numeric.slice(0, 2)}/${numeric.slice(2, 4)}/${numeric.slice(4, 8)}`;

    case 'currency':
      // Format: xxx,xxx,xxx,xxx (commas every 3 digits from right)
      if (!numeric) return '';
      return numeric.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    case 'percentage': {
      // Format: 0-100 with up to 2 decimal places
      // Allow decimals in the input
      const numericWithDecimal = value.replace(/[^\d.]/g, '');

      // Prevent multiple decimal points
      const parts = numericWithDecimal.split('.');
      if (parts.length > 2) return value.slice(0, -1);

      // Limit to 2 decimal places
      if (parts[1] && parts[1].length > 2) {
        return `${parts[0]}.${parts[1].slice(0, 2)}`;
      }

      // Validate range 0-100
      const numValue = parseFloat(numericWithDecimal);
      if (!isNaN(numValue) && numValue > 100) {
        return '100';
      }

      return numericWithDecimal;
    }

    case 'none':
    default:
      return value;
  }
};

/**
 * Get validation pattern for format
 * @param format - The format type
 * @returns The RegExp pattern for validation or undefined if no pattern
 */
export const getPatternForFormat = (
  format: InputFormat
): RegExp | undefined => {
  switch (format) {
    case 'phone':
      return /^\d{3}-\d{3}-\d{4}$/;
    case 'ssn':
      return /^\d{3}-\d{2}-\d{4}$/;
    case 'creditCard':
      return /^\d{4} \d{4} \d{4} \d{4}$/;
    case 'zipCode':
      return /^\d{5}(-\d{4})?$/;
    case 'date':
      return /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    case 'currency':
      return /^\d{1,3}(,\d{3})*$/;
    case 'percentage':
      return /^(100(\.0{1,2})?|[0-9]{1,2}(\.\d{1,2})?)$/;
    default:
      return undefined;
  }
};

/**
 * Get placeholder for format
 * @param format - The format type
 * @returns The placeholder string for the format
 */
export const getPlaceholderForFormat = (format: InputFormat): string => {
  switch (format) {
    case 'phone':
      return '555-123-4567';
    case 'ssn':
      return '123-45-6789';
    case 'creditCard':
      return '1234 5678 9012 3456';
    case 'zipCode':
      return '12345-6789';
    case 'date':
      return 'MM/DD/YYYY';
    case 'currency':
      return '100,000,000';
    case 'percentage':
      return '20';
    default:
      return '';
  }
};
